import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useReportError, extractErrorInfo } from '@features/support';
import { customerService } from '../services/customerService';
import type { CustomerListParams, CreateCustomerInput } from '../types/customer.types';

export function useCustomers(params?: CustomerListParams) {
  // Clean params - remove undefined values to ensure proper cache keys
  const cleanParams = params ? {
    page: params.page,
    limit: params.limit,
    ...(params.name && { name: params.name }),
    ...(params.taxId && { taxId: params.taxId }),
  } : undefined;

  console.log('🔑 useCustomers - Query Key:', ['customers', cleanParams]);

  return useQuery({
    queryKey: ['customers', cleanParams],
    queryFn: () => {
      console.log('🔄 useCustomers - Fetching with params:', cleanParams);
      return customerService.getCustomers(cleanParams);
    },
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => customerService.getCustomerById(id),
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  // Asistente de soporte: al fallar el alta, el error se le muestra al cliente con
  // la explicacion del agente en vez de un mensaje crudo del backend.
  const reportError = useReportError();

  return useMutation({
    mutationFn: (customer: CreateCustomerInput) => {
      console.log('🔵 useCreateCustomer - mutationFn called with:', customer);
      return customerService.createCustomer(customer);
    },
    onSuccess: (data) => {
      console.log('✅ useCreateCustomer - Success:', data);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
    onError: (error: any) => {
      console.error('❌ useCreateCustomer - Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error,
      });
      reportError({ title: 'Error al crear cliente', screen: 'crear cliente', ...extractErrorInfo(error) });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateCustomerInput }) =>
      customerService.updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerService.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}
